import React, {FC, useState} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorIcon, SuccessIcon, WarningIcon} from '../../../components/Icons';
import PopupCloseButton from '../../../components/Popup/components/PopupCloseButton/PopupCloseButton';
import appConfig from '../../../config/appConfig';
import standaloneLocalization from '../../../config/standaloneConfig/standaloneLocalization';
import * as notificationTypes from '../../../consts/app/notificationTypes';
import * as localizationKeys from '../../../consts/localization/localizationKeys';
import NotificationModalTypes from '../../../enums/app/notificationModalTypes';
import {getLocalizedStrings} from '../../../services/localization/localizationService';
import {removeNotification} from '../../../state/ducks/global';
import helpers from '../../../utils/helpers';
import {twx} from '../../../utils/tailwindUtils';
import LeviaGetStartedNotification from './components/LeviaGetStartedNotification/LeviaGetStartedNotification';
import PromotedRegistrationNotification from './components/PromotedRegistrationNotification/PromotedRegistrationNotification';
import QsgDevicePairedNotification from './components/QsgDevicePairedNotification/QsgDevicePairedNotification';
import ReconnectNotification from './components/ReconnectNotification/ReconnectNotification';
import RenameNotification from './components/RenameNotification/RenameNotification';
import {INotification} from './NotificationTypes';
import useHideNotification from './useHideNotification';

interface IProps {
    notification: INotification;
    onClose?: () => void;
}

const Notification: FC<IProps> = (props) => {
    const {notification, onClose} = props;
    const {isInline, notificationId, text, type, testId, notificationType, autoFocus} = notification;
    const [isActive, setIsActive] = useState(false);
    const dispatch = useDispatch();
    const localizedStrings = getLocalizedStrings();
    let icon;
    const iconStyles = 'w-6 h-6 shrink-0';
    const toastNotificationDisplayTime = appConfig.getToastNotificationDisplayTime();

    switch (type) {
        case notificationTypes.FAILED:
            icon = <ErrorIcon className={iconStyles} isForceHide />;
            break;

        case notificationTypes.SUCCESS:
            icon = <SuccessIcon className={iconStyles} />;
            break;

        case notificationTypes.INFO:
            icon = <WarningIcon className={iconStyles} isForceHide />;
            break;

        case notificationTypes.CUSTOM:
            icon = null;
            break;

        default:
            break;
    }

    const onNotificationClick = () => {
        if (!isActive) {
            setIsActive(true);

            setTimeout(() => {
                hideNotification();
                helpers.runFunction(onClose);
            }, 500);
        }
    };

    const hideNotification = () => {
        dispatch(removeNotification(notificationId));
    };

    let customNotification;

    switch (notificationType) {
        case NotificationModalTypes.RECONNECT_DEVICE:
            customNotification = (
                <ReconnectNotification onClick={onNotificationClick} hideNotification={hideNotification} />
            );
            break;
        case NotificationModalTypes.RENAME_DEVICE:
            customNotification = <RenameNotification />;
            break;
        case NotificationModalTypes.PROMOTED_REGISTRATION:
            customNotification = <PromotedRegistrationNotification />;
            break;
        case NotificationModalTypes.LEVIA_GET_STARTED_NOTIFICATION:
            customNotification = <LeviaGetStartedNotification onClick={onNotificationClick} />;
            break;
        case NotificationModalTypes.QSG_DEVICE_PAIRED_NOTIFICATION:
            customNotification = <QsgDevicePairedNotification hideNotification={hideNotification} />;
            break;
    }

    useHideNotification({
        toastNotificationDisplayTime,
        hideNotification,
    });

    return (
        <div
            className={twx(
                'relative flex w-full items-center gap-4 rounded bg-secondary py-5 pl-6 pr-11 shadow-md lg:py-7',
                {
                    'animate-hide': isActive,
                    'rounded-xl py-4 pr-12 shadow-none lg:py-4': isInline,
                }
            )}
            data-testid={testId}
        >
            {icon}
            <div
                className='leading-14_3 flex-1 text-start text-14 tracking-04 text-primary'
                role={autoFocus ? '' : 'alert'}
            >
                {customNotification ? customNotification : text}
            </div>
            <PopupCloseButton
                ariaLabel={
                    localizedStrings
                        ? localizedStrings[localizationKeys.CLOSE_NOTIFICATION_ARIA_LABEL]
                        : standaloneLocalization.CLOSE_NOTIFICATION_ARIA_LABEL
                }
                className={twx('absolute top-0 block h-4 w-4 p-[22px] ltr:right-0 rtl:left-0 lg:m-3 lg:p-0', {
                    'm-[21px]': isInline,
                })}
                onClick={onNotificationClick}
            />
        </div>
    );
};

export default Notification;
